// web-next/src/features/upstream-auth/store.tsx
import { createContext, onCleanup, useContext, type JSX, type ParentProps } from "solid-js";
import { createSignal, type Accessor } from "solid-js";
import {
  eventsWsUrl,
  reauthWsUrl,
  logoutSession as apiLogoutSession,
  refreshToken as apiRefreshToken,
  startReauth as apiStartReauth,
  type ReauthFrame,
  type ReauthInput,
  type ReauthStatus,
  type StartReauthResult,
  type UpstreamSnapshot,
  type UpstreamState,
} from "./api";

export type { UpstreamState };

export interface UpstreamStore {
  state: Accessor<UpstreamState>;
  tokenExpiresAt: Accessor<string | null>;
  startReauth(): Promise<StartReauthResult>;
  endReauth(): void;
  refreshToken(): Promise<void>;
  logout(): Promise<void>;
  streamFrames: Accessor<ReauthFrame | null>;
  reauthStatus: Accessor<ReauthStatus>;
  sendInput(msg: ReauthInput): void;
  // Live cache state pushed from the backend via /api/events. null
  // until the WS sends its first cache-count message.
  cacheCount: Accessor<number | null>;
}

const Ctx = createContext<UpstreamStore | null>(null);

// Translate the snapshot's server-clock timestamps into the client's
// time domain so the UI's `Date.now()`-driven timers stay correct under
// clock skew. Without this, a client whose clock lagged the server's
// saw `healthySinceAt` as being in the future, and `formatElapsed`
// (which uses `Math.abs`) masked the negative delta — the session
// "elapsed" value counted DOWN until clocks aligned then flipped to
// counting up. Same hazard applies to `tokenExpiresAt`.
//
// `skewMs = clientNow - serverNow` (positive when the client clock is
// ahead). To produce a client-time-equivalent of a server timestamp T,
// we add skewMs: T_client = T_server + (clientNow - serverNow).
export function translateSnapshot(snapshot: UpstreamSnapshot, clientNow: number): UpstreamSnapshot {
  // Tolerate a missing / unparseable `serverNow` — older snapshots in
  // flight during a deploy, or hand-built test fixtures, should still
  // surface their state instead of throwing. Skew correction silently
  // becomes a no-op.
  const serverNowMs = snapshot.serverNow ? new Date(snapshot.serverNow).getTime() : NaN;
  if (!Number.isFinite(serverNowMs)) return snapshot;
  const skewMs = clientNow - serverNowMs;
  if (skewMs === 0) return snapshot;
  const shift = (iso: string): string => new Date(new Date(iso).getTime() + skewMs).toISOString();
  let state: UpstreamState;
  switch (snapshot.state.kind) {
    case "healthy":
      state = {
        kind: "healthy",
        expiresAt: shift(snapshot.state.expiresAt),
        healthySinceAt: shift(snapshot.state.healthySinceAt),
      };
      break;
    case "reauth-needed":
      state = { kind: "reauth-needed", lastErrorAt: shift(snapshot.state.lastErrorAt) };
      break;
    case "reauth-in-progress":
      state = {
        kind: "reauth-in-progress",
        driverEmail: snapshot.state.driverEmail,
        startedAt: shift(snapshot.state.startedAt),
      };
      break;
    case "disabled":
      state = snapshot.state;
      break;
  }
  return {
    state,
    tokenExpiresAt: snapshot.tokenExpiresAt ? shift(snapshot.tokenExpiresAt) : null,
    serverNow: new Date(clientNow).toISOString(),
  };
}

export interface CreateUpstreamStoreOptions {
  // Fired on the backend's cache-cleared broadcast. The page-level
  // decision (reload) belongs to the App layer, not a store handler.
  onCacheCleared?: () => void;
}

export function createUpstreamStore(opts: CreateUpstreamStoreOptions = {}): UpstreamStore & { dispose(): void } {
  const [state, setState] = createSignal<UpstreamState>({ kind: "disabled", reason: "not-yet-loaded" });
  const [tokenExpiresAt, setTokenExpiresAt] = createSignal<string | null>(null);
  const [streamFrames, setStreamFrames] = createSignal<ReauthFrame | null>(null);
  const [reauthStatus, setReauthStatus] = createSignal<ReauthStatus>({ kind: "connecting" });
  const [cacheCount, setCacheCount] = createSignal<number | null>(null);
  let reauthWS: WebSocket | null = null;

  // Reconnecting events WS — handles owned so dispose() can stop the
  // socket + the retry chain (tests, provider unmount, HMR).
  let backoffMs = 1000;
  let eventsWS: WebSocket | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let disposed = false;
  function connectEvents(): void {
    if (disposed) return;
    const ws = new WebSocket(eventsWsUrl());
    eventsWS = ws;
    ws.onmessage = (e) => {
      try {
        const f = JSON.parse(e.data) as { type: string; snapshot?: UpstreamSnapshot; count?: number };
        if (f.type === "upstream-state" && f.snapshot) {
          const adjusted = translateSnapshot(f.snapshot, Date.now());
          setState(adjusted.state);
          setTokenExpiresAt(adjusted.tokenExpiresAt);
        }
        if (f.type === "cache-count" && typeof f.count === "number") {
          setCacheCount(f.count);
        }
        if (f.type === "cache-cleared") {
          opts.onCacheCleared?.();
        }
      } catch { /* ignore */ }
    };
    ws.onopen = () => { backoffMs = 1000; };
    ws.onclose = () => {
      if (disposed) return;
      reconnectTimer = setTimeout(connectEvents, backoffMs);
      backoffMs = Math.min(backoffMs * 2, 10000);
    };
    ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
  }
  connectEvents();

  function dispose(): void {
    disposed = true;
    if (reconnectTimer !== null) clearTimeout(reconnectTimer);
    try { eventsWS?.close(); } catch { /* ignore */ }
    try { reauthWS?.close(); } catch { /* ignore */ }
  }

  async function startReauth(): Promise<StartReauthResult> {
    endReauth();
    setStreamFrames(null);
    setReauthStatus({ kind: "connecting" });
    const r = await apiStartReauth();
    if (!("ok" in r)) {
      setReauthStatus(r.error === "locked" ? { kind: "locked", driverEmail: r.driverEmail } : { kind: "unavailable" });
      return r;
    }
    const ws = new WebSocket(reauthWsUrl());
    reauthWS = ws;
    ws.onmessage = (e) => {
      let f: ReauthFrame;
      try { f = JSON.parse(e.data) as ReauthFrame; } catch { return; }
      setStreamFrames(f);
      if (f.type !== "status") return;
      if (f.status.kind === "ready") setReauthStatus({ kind: "streaming" });
      else if (f.status.kind === "completed") setReauthStatus({ kind: "completed" });
      else setReauthStatus({ kind: "ended", reason: f.status.reason });
    };
    ws.onclose = (e) => {
      if (reauthWS === ws) reauthWS = null;
      const k = reauthStatus().kind;
      // Server dropped the stream without a terminal status (rejected attach, network).
      if (k === "connecting" || k === "streaming") setReauthStatus({ kind: "ended", reason: e.reason || "connection lost" });
    };
    return r;
  }

  function endReauth(): void {
    const ws = reauthWS;
    reauthWS = null;
    if (!ws) return;
    ws.onclose = null;
    ws.onmessage = null;
    ws.close();
  }

  async function refreshToken(): Promise<void> {
    await apiRefreshToken();
    // State will update via the /api/events broadcast triggered by
    // the orchestrator's setState fan-out.
  }

  async function logout(): Promise<void> {
    await apiLogoutSession();
  }

  function sendInput(msg: ReauthInput): void {
    if (reauthWS && reauthWS.readyState === WebSocket.OPEN) {
      reauthWS.send(JSON.stringify(msg));
    }
  }

  return { state, tokenExpiresAt, startReauth, endReauth, refreshToken, logout, streamFrames, reauthStatus, sendInput, cacheCount, dispose };
}

export function UpstreamProvider(props: ParentProps): JSX.Element {
  const store = createUpstreamStore({
    // Another client (or this one) wiped the backend cache; every
    // connected dashboard reloads so the user sees fresh data.
    onCacheCleared: () => window.location.reload(),
  });
  onCleanup(() => store.dispose());
  return <Ctx.Provider value={store}>{props.children}</Ctx.Provider>;
}

export function useUpstream(): UpstreamStore {
  const v = useContext(Ctx);
  if (!v) throw new Error("useUpstream must be used inside <UpstreamProvider>");
  return v;
}
